// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Group quote builder widget for Ask LodgIQ. Opened by the agent's
// show_group_quote tool (ideally after evaluate_group, whose displacement floor
// is passed through). The user edits the quote, sees live totals and a
// below-floor warning, and prints/exports a branded quote for the sales team.

import React, { useMemo, useState } from "react";

function moneyFmt(currency, dp = 2) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: dp, minimumFractionDigits: dp > 0 ? 2 : 0 });
  } catch (_) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: dp });
  }
}

const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const nice = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const DEFAULT_TERMS =
  "Rate is per room, per night, exclusive of taxes and fees. " +
  "Subject to availability at the time of contracting. " +
  "A signed agreement and deposit are required to confirm the block.";

export default function GroupQuoteCard({ widget, hotelName, onInterpret }) {
  const p = (widget && widget.prefill) || {};
  const money = moneyFmt(widget && widget.currency);
  const hotel = (widget && widget.hotel && widget.hotel.name) || hotelName || "";
  const floor = p.min_acceptable_rate != null ? Number(p.min_acceptable_rate) : null;

  const [q, setQ] = useState(() => ({
    group_name: p.group_name || "",
    contact: "",
    from_date: p.from_date || "",
    to_date: p.to_date || p.from_date || "",
    rooms: p.group_rooms != null ? String(p.group_rooms) : "",
    rate: p.group_rate != null ? String(p.group_rate) : "",
    ancillary: p.ancillary_per_room_night != null ? String(p.ancillary_per_room_night) : "",
    valid_until: addDays(new Date().toISOString().slice(0, 10), 14),
    terms: DEFAULT_TERMS,
  }));
  const set = (k) => (e) => setQ((s) => ({ ...s, [k]: e.target.value }));

  const calc = useMemo(() => {
    const from = new Date(`${q.from_date}T12:00:00`);
    const to = new Date(`${q.to_date}T12:00:00`);
    const nights = Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())
      ? 0
      : Math.max(0, Math.round((to - from) / 86400000) + 1);   // inclusive stay nights
    const rooms = Number(q.rooms) || 0;
    const rate = Number(q.rate) || 0;
    const anc = Number(q.ancillary) || 0;
    const roomNights = nights * rooms;
    const roomRevenue = roomNights * rate;
    const ancillary = roomNights * anc;
    return { nights, roomNights, roomRevenue, ancillary, total: roomRevenue + ancillary, rate };
  }, [q]);

  const belowFloor = floor != null && calc.rate > 0 && calc.rate < floor;

  const review = () => {
    if (!onInterpret) return;
    onInterpret(
      `Review this group quote before I send it: "${q.group_name || "unnamed group"}", ` +
      `${q.rooms} rooms/night, ${nice(q.from_date)} to ${nice(q.to_date)} (${calc.nights} nights, ` +
      `${calc.roomNights} room-nights) at ${money.format(calc.rate)} — total room revenue ${money.format(calc.roomRevenue)}` +
      (calc.ancillary ? ` plus est. ancillary ${money.format(calc.ancillary)}` : "") +
      (floor != null ? `. Displacement floor rate is ${money.format(floor)}.` : ".") +
      " Any concerns or negotiation advice?"
    );
  };

  const printQuote = () => {
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return;
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    w.document.write(`<!doctype html><html><head><title>Group quote — ${esc(q.group_name || hotel)}</title>
<style>
  body { font-family: "Segoe UI", -apple-system, Arial, sans-serif; color: #333; margin: 44px 52px; }
  .head { border-bottom: 3px solid #01a4ef; padding-bottom: 14px; display: flex; justify-content: space-between; align-items: baseline; }
  .hotel { font-size: 21px; font-weight: 700; color: #00415f; }
  .tag { font-size: 11px; color: #7a8894; letter-spacing: 1.5px; text-transform: uppercase; }
  h1 { font-size: 16px; color: #00415f; margin: 26px 0 4px; }
  .sub { font-size: 12px; color: #7a8894; margin-bottom: 20px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px; }
  td { padding: 8px 10px; border-bottom: 1px solid #e8ecef; }
  td:first-child { color: #7a8894; width: 42%; }
  td:last-child { text-align: right; font-weight: 600; color: #333; }
  .total td { border-top: 2px solid #01a4ef; border-bottom: none; font-size: 15px; }
  .total td:last-child { color: #00415f; }
  .terms { margin-top: 26px; font-size: 11.5px; color: #66707a; line-height: 1.6; }
  .terms b { color: #333; }
  .foot { margin-top: 40px; padding-top: 12px; border-top: 1px solid #e8ecef; font-size: 10px; color: #9aa5ae; display: flex; justify-content: space-between; }
  @media print { body { margin: 24px 28px; } }
</style></head><body>
  <div class="head"><span class="hotel">${esc(hotel)}</span><span class="tag">Group rate quote</span></div>
  <h1>${esc(q.group_name || "Group proposal")}</h1>
  <div class="sub">Prepared ${esc(new Date().toLocaleDateString())} · Valid until ${esc(nice(q.valid_until))}${q.contact ? ` · Attn: ${esc(q.contact)}` : ""}</div>
  <table>
    <tr><td>Stay dates</td><td>${esc(nice(q.from_date))} – ${esc(nice(q.to_date))} (${calc.nights} night${calc.nights === 1 ? "" : "s"})</td></tr>
    <tr><td>Rooms per night</td><td>${esc(q.rooms)}</td></tr>
    <tr><td>Total room-nights</td><td>${calc.roomNights}</td></tr>
    <tr><td>Quoted rate (per room, per night)</td><td>${money.format(calc.rate)}</td></tr>
    <tr><td>Room revenue</td><td>${money.format(calc.roomRevenue)}</td></tr>
    ${calc.ancillary ? `<tr><td>Estimated ancillary (F&amp;B, banquet)</td><td>${money.format(calc.ancillary)}</td></tr>` : ""}
    <tr class="total"><td>Total estimated value</td><td>${money.format(calc.total)}</td></tr>
  </table>
  <div class="terms"><b>Terms.</b> ${esc(q.terms)}</div>
  <div class="foot"><span>${esc(hotel)}</span><span>Prepared with LodgIQ</span></div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (_) {} }, 250);
  };

  if (widget && widget.schema_version && widget.schema_version !== 1) {
    return <div className="wiz-calc wiz-calc--fallback">This quote builder needs a newer app version. Please refresh.</div>;
  }

  return (
    <div className="wiz-calc wiz-quote">
      <div className="wiz-calc__head">
        <span className="wiz-calc__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5M9 13h6M9 17h4" />
          </svg>
        </span>
        <span className="wiz-calc__title">Group quote</span>
        {q.group_name ? <span className="wiz-sim__date">{q.group_name}</span> : null}
      </div>

      <div className="wiz-calc__fields">
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Group / company</span>
          <input type="text" value={q.group_name} onChange={set("group_name")} placeholder="Acme Corp annual meeting" />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Contact (attn.)</span>
          <input type="text" value={q.contact} onChange={set("contact")} placeholder="optional" />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">First night<em>*</em></span>
          <input type="date" value={q.from_date} onChange={set("from_date")} />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Last night<em>*</em></span>
          <input type="date" value={q.to_date} onChange={set("to_date")} />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Rooms / night<em>*</em></span>
          <input type="number" value={q.rooms} onChange={set("rooms")} />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Rate / room-night<em>*</em></span>
          <input type="number" value={q.rate} onChange={set("rate")} />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Ancillary / room-night</span>
          <input type="number" value={q.ancillary} onChange={set("ancillary")} placeholder="F&B, banquet…" />
        </label>
        <label className="wiz-calc__field">
          <span className="wiz-calc__flabel">Quote valid until</span>
          <input type="date" value={q.valid_until} onChange={set("valid_until")} />
        </label>
      </div>

      <label className="wiz-calc__field wiz-quote__terms">
        <span className="wiz-calc__flabel">Terms</span>
        <textarea rows={3} value={q.terms} onChange={set("terms")} />
      </label>

      {floor != null && (
        <div className={`wiz-calc__verdict wiz-calc__verdict--${belowFloor ? "bad" : "good"}`}>
          {belowFloor
            ? `Quoted rate ${money.format(calc.rate)} is BELOW the displacement floor ${money.format(floor)} — this deal dilutes revenue.`
            : `Quoted rate clears the displacement floor ${money.format(floor)}${p.net_revenue_impact != null ? ` · est. net impact ${money.format(Number(p.net_revenue_impact))}` : ""}.`}
        </div>
      )}

      <div className="wiz-calc__metrics wiz-quote__totals">
        <div className="wiz-calc__metric">
          <span className="wiz-calc__metric-label">Room-nights</span>
          <span className="wiz-calc__metric-val">{calc.roomNights}</span>
          <span className="wiz-sim__delta">{calc.nights} night{calc.nights === 1 ? "" : "s"}</span>
        </div>
        <div className="wiz-calc__metric">
          <span className="wiz-calc__metric-label">Room revenue</span>
          <span className="wiz-calc__metric-val">{money.format(calc.roomRevenue)}</span>
        </div>
        <div className="wiz-calc__metric">
          <span className="wiz-calc__metric-label">Total est. value</span>
          <span className="wiz-calc__metric-val">{money.format(calc.total)}</span>
          {calc.ancillary > 0 && <span className="wiz-sim__delta is-up">incl. ancillary</span>}
        </div>
      </div>

      <div className="wiz-quote__actions">
        <button className="wiz-calc__run" onClick={printQuote} disabled={!calc.roomNights || !calc.rate}>
          Print / save as PDF
        </button>
        {onInterpret && (
          <button className="wiz-calc__interpret" onClick={review}>Ask LodgIQ to review ↗</button>
        )}
      </div>

      <div className="wiz-calc__foot">© {new Date().getFullYear()} LodgIQ</div>
    </div>
  );
}
